import { invalidate } from '@react-three/fiber';
import { useEffect, type RefObject } from 'react';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { yieldToMain } from 'app/modules/wallforming/components/state-management/placement/utils/schedulerUtils';
import { DRAGGING_BOX_THRESHOLD, MATRIX_UPDATE_CHUNK_SIZE } from 'shared/constants';

import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

interface InstanceSyncHandlerProps {
    isMainCanvas: boolean;
    articleRef: RefObject<ArticleReference>;
    instanceSelectionIdsRef: RefObject<string[]>;
    chunkUpdateInProgressRef: RefObject<boolean>;
    chunkUpdateIdRef: RefObject<number>;
}

/**
 * Subscribes to article instance changes and keeps drag/selection/cursor state in sync with the store.
 */
export const useInstanceSyncHandler = ({
    isMainCanvas,
    articleRef,
    instanceSelectionIdsRef,
    chunkUpdateInProgressRef,
    chunkUpdateIdRef
}: InstanceSyncHandlerProps) => {
    const {
        isAutoDragActiveRef,
        isZAxisDragActiveRef,
        registerArticleDragReference,
        updateInstanceMatrices,
        isDraggingActive,
        syncSelectedInstancesToCursor
    } = usePlacementStore(
        useShallow((state) => ({
            isAutoDragActiveRef: state.isAutoDragActiveRef,
            isZAxisDragActiveRef: state.isZAxisDragActiveRef,
            registerArticleDragReference: state.registerArticleDragReference,
            updateInstanceMatrices: state.updateInstanceMatrices,
            isDraggingActive: state.isDraggingActive,
            syncSelectedInstancesToCursor: state.syncSelectedInstancesToCursor
        }))
    );

    useEffect(() => {
        const subscribedArticleInstancesCount = usePlacementStore.subscribe(
            (state) => Object.keys(state.articles[articleRef.current.id]?.articleInstances ?? {}).length,
            () => {
                if (isAutoDragActiveRef.current && !isZAxisDragActiveRef.current) {
                    syncSelectedInstancesToCursor();
                    invalidate();
                }

                requestAnimationFrame(() => {
                    const state = usePlacementStore.getState();
                    const currentSelections = state.articleInstanceSelections;
                    const currentSelectionIds = Object.keys(currentSelections);

                    if (isDraggingActive()) {
                        const selectedInstanceIds = currentSelectionIds.filter(
                            (id) => id in articleRef.current.articleInstances
                        );

                        if (selectedInstanceIds.length > 0) {
                            instanceSelectionIdsRef.current = selectedInstanceIds;

                            if (isMainCanvas) {
                                registerArticleDragReference(
                                    articleRef.current.id,
                                    articleRef as RefObject<ArticleReference | undefined>
                                );
                            }

                            if (
                                state.isAutoDragActiveRef.current &&
                                state.mainWebGLRendererRef.current?.domElement.style.cursor === 'wait'
                            ) {
                                state.mainWebGLRendererRef.current.domElement.style.cursor = 'move';
                            }

                            const totalSelectionCount = currentSelectionIds.length;
                            const activateDraggingBox =
                                state.isAutoDragActiveRef.current && totalSelectionCount > DRAGGING_BOX_THRESHOLD;

                            const isDraggingBoxActive =
                                state.isAutoDragActiveRef.current &&
                                totalSelectionCount > DRAGGING_BOX_THRESHOLD &&
                                state.draggingBoxRef.current.isActive;

                            if (!isDraggingBoxActive && !activateDraggingBox) {
                                updateInstanceMatrices(true, articleRef, false, selectedInstanceIds);
                            }
                        }
                    } else {
                        const hasSelectedInstances = currentSelectionIds.some(
                            (id) => id in articleRef.current.articleInstances
                        );

                        const articleInstanceIds = Object.keys(articleRef.current.articleInstances);

                        if (articleInstanceIds.length <= MATRIX_UPDATE_CHUNK_SIZE) {
                            updateInstanceMatrices(hasSelectedInstances, articleRef, false, articleInstanceIds);
                        } else {
                            // chunked update to avoid blocking the main thread
                            const generation = ++chunkUpdateIdRef.current;
                            chunkUpdateInProgressRef.current = true;

                            (async () => {
                                for (let i = 0; i < articleInstanceIds.length; i += MATRIX_UPDATE_CHUNK_SIZE) {
                                    if (chunkUpdateIdRef.current !== generation) {
                                        break;
                                    }

                                    const chunk = articleInstanceIds.slice(i, i + MATRIX_UPDATE_CHUNK_SIZE);
                                    const isLast = i + MATRIX_UPDATE_CHUNK_SIZE >= articleInstanceIds.length;
                                    updateInstanceMatrices(hasSelectedInstances, articleRef, false, chunk);

                                    if (!isLast) {
                                        await yieldToMain();
                                    }
                                }
                                chunkUpdateInProgressRef.current = false;
                            })();
                        }
                    }
                });
            }
        );

        return () => subscribedArticleInstancesCount();
    }, [
        articleRef,
        isZAxisDragActiveRef,
        updateInstanceMatrices,
        registerArticleDragReference,
        isAutoDragActiveRef,
        isDraggingActive,
        instanceSelectionIdsRef,
        syncSelectedInstancesToCursor,
        isMainCanvas,
        chunkUpdateIdRef,
        chunkUpdateInProgressRef
    ]);
};
